import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import ServerGrid from "../components/ServerGrid";
import ServerDetail from "../components/ServerDetail";
import Settings from "../components/Settings";
import AddServerModal from "../components/AddServerModal";
import AddGroupModal from "../components/AddGroupModal";
import {
  Search,
  Plus,
  Moon,
  Sun,
  Settings as SettingsIcon,
  LogOut,
  Server,
  Wifi,
  WifiOff,
  FolderPlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, visualTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [servers, setServers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null = all, 'online', 'offline'
  const [showAddServer, setShowAddServer] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);

  const fetchServers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/servers`);
      setServers(response.data);
    } catch (error) {
      console.error("Failed to fetch servers:", error);
      toast.error("Failed to load servers");
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchServers(), fetchGroups()]);
      setLoading(false);
    };
    loadData();
  }, [fetchServers, fetchGroups]);

  // Auto-refresh servers every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchServers, 30000);
    return () => clearInterval(interval);
  }, [fetchServers]);

  const handleAddServer = async (serverData) => {
    try {
      const response = await axios.post(`${API}/servers`, serverData);
      setServers((prev) => [...prev, response.data]);
      setShowAddServer(false);
      toast.success("Server added");
    } catch (error) {
      console.error("Failed to add server:", error);
      toast.error("Failed to add server");
    }
  };

  const handleUpdateServer = async (serverId, serverData) => {
    try {
      const response = await axios.put(`${API}/servers/${serverId}`, serverData);
      setServers((prev) =>
        prev.map((s) => (s.id === serverId ? response.data : s))
      );
      setEditingServer(null);
      toast.success("Server updated");
    } catch (error) {
      console.error("Failed to update server:", error);
      toast.error("Failed to update server");
    }
  };

  const handleDeleteServer = async (serverId) => {
    try {
      await axios.delete(`${API}/servers/${serverId}`);
      setServers((prev) => prev.filter((s) => s.id !== serverId));
      toast.success("Server deleted");
      if (location.pathname.includes(serverId)) {
        navigate("/");
      }
    } catch (error) {
      console.error("Failed to delete server:", error);
      toast.error("Failed to delete server");
    }
  };

  const handleCheckServer = async (serverId) => {
    try {
      const response = await axios.post(`${API}/servers/${serverId}/check`);
      setServers((prev) =>
        prev.map((s) => (s.id === serverId ? response.data : s))
      );
      toast.success("Checking...");
    } catch (error) {
      console.error("Failed to check server:", error);
      toast.error("Failed to check server");
    }
  };

  const handleToggleFavorite = async (serverId) => {
    try {
      const response = await axios.post(`${API}/servers/${serverId}/favorite`);
      setServers((prev) =>
        prev.map((s) => (s.id === serverId ? response.data : s))
      );
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      toast.error("Failed to update favorite");
    }
  };

  const handleToggleCritical = async (serverId) => {
    try {
      const response = await axios.post(`${API}/servers/${serverId}/critical`);
      setServers((prev) =>
        prev.map((s) => (s.id === serverId ? response.data : s))
      );
      const isCritical = response.data.is_critical;
      toast.success(isCritical ? "Server marked as critical" : "Critical flag removed");
    } catch (error) {
      console.error("Failed to toggle critical:", error);
      toast.error("Failed to update critical status");
    }
  };

  const handleAddGroup = async (groupData) => {
    try {
      const response = await axios.post(`${API}/groups`, groupData);
      setGroups((prev) => [...prev, response.data]);
      setShowAddGroup(false);
      toast.success("Group created");
    } catch (error) {
      console.error("Failed to create group:", error);
      toast.error("Failed to create group");
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/groups/${groupId}`);
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      toast.success("Group deleted");
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error("Failed to delete group");
    }
  };

  const handleEditGroup = async (groupData) => {
    try {
      const response = await axios.put(`${API}/groups/${groupData.id}`, { name: groupData.name });
      setGroups((prev) =>
        prev.map((g) => (g.id === groupData.id ? response.data : g))
      );
      setEditingGroup(null);
      toast.success("Group renamed");
    } catch (error) {
      console.error("Failed to rename group:", error);
      toast.error("Failed to rename group");
    }
  };

  const handleReorderServers = async (serverIds) => {
    try {
      await axios.post(`${API}/servers/reorder`, { item_ids: serverIds });
      // Update local state with new order
      setServers(prev => {
        const serverMap = new Map(prev.map(s => [s.id, s]));
        return serverIds
          .map((id, index) => {
            const server = serverMap.get(id);
            return server ? { ...server, sort_order: index } : null;
          })
          .filter(Boolean)
          .concat(prev.filter(s => !serverIds.includes(s.id)));
      });
    } catch (error) {
      console.error("Failed to reorder servers:", error);
      toast.error("Failed to save order");
    }
  };

  const handleReorderGroups = async (groupIds) => {
    try {
      await axios.post(`${API}/groups/reorder`, { item_ids: groupIds });
      // Update local state with new order
      setGroups(prev => {
        const groupMap = new Map(prev.map(g => [g.id, g]));
        return groupIds
          .map((id, index) => {
            const group = groupMap.get(id);
            return group ? { ...group, sort_order: index } : null;
          })
          .filter(Boolean);
      });
    } catch (error) {
      console.error("Failed to reorder groups:", error);
      toast.error("Failed to save order");
    }
  };

  // Filter servers
  const filteredServers = servers.filter((server) => {
    const matchesSearch =
      !searchQuery ||
      server.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.addresses?.some((a) =>
        a.url.toLowerCase().includes(searchQuery.toLowerCase())
      );
    
    const matchesStatus = 
      !statusFilter || 
      server.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: servers.length,
    online: servers.filter((s) => s.status === "online").length,
    offline: servers.filter((s) => s.status === "offline").length,
  };

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setStatusFilter(prev => prev === status ? null : status);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto"></div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Cyber theme background effects */}
      {visualTheme === 'cyber' && (
        <>
          <div className="fixed inset-0 cyber-grid pointer-events-none opacity-30" />
          <div className="fixed top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl pointer-events-none" />
        </>
      )}

      {/* Header */}
      <header className="glass-header h-16 px-6 flex items-center justify-between gap-4 relative">
        {/* Cyber decorative scan line */}
        {visualTheme === 'cyber' && (
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        )}
        
        {/* Left - Logo */}
        <div className="flex items-center gap-4">
          <div 
            className="flex items-center gap-2 cursor-pointer group" 
            onClick={() => navigate("/")}
            data-testid="logo-link"
          >
            <img 
              src="/netset-logo.svg" 
              alt="Netset" 
              className={`h-5 w-auto transition-all ${
                visualTheme === 'cyber' 
                  ? 'brightness-0 invert drop-shadow-[0_0_8px_rgba(0,240,255,0.5)] group-hover:drop-shadow-[0_0_15px_rgba(0,240,255,0.7)]'
                  : 'dark:brightness-0 dark:invert'
              }`}
            />
          </div>
          {visualTheme === 'cyber' && (
            <div className="hidden md:block text-xs font-mono text-cyan-400/50">
              // CONTROL PANEL
            </div>
          )}
        </div>

        {/* Center - Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              visualTheme === 'cyber' ? 'text-cyan-400/50' : 'text-muted-foreground'
            }`} />
            <Input
              data-testid="search-input"
              type="text"
              placeholder={visualTheme === 'cyber' ? "Search systems..." : "Search..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-9 h-9 text-sm ${
                visualTheme === 'cyber' 
                  ? 'bg-cyan-500/5 border-cyan-500/20 text-cyan-100 placeholder:text-cyan-400/30 focus:border-cyan-500/50'
                  : 'bg-muted/50 border-0'
              }`}
            />
          </div>
        </div>

        {/* Right - Stats & Actions */}
        <div className="flex items-center gap-2">
          {/* Quick Stats - Clickable Filters */}
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <button
              data-testid="filter-online-btn"
              onClick={() => toggleStatusFilter('online')}
              className={`stats-widget cursor-pointer transition-all ${
                statusFilter === 'online' 
                  ? visualTheme === 'cyber'
                    ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-background bg-green-500/20 border-green-400/50'
                    : 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-background bg-emerald-500/10' 
                  : visualTheme === 'cyber'
                    ? 'hover:bg-green-500/10 hover:border-green-400/30'
                    : 'hover:bg-emerald-500/10'
              }`}
              title={statusFilter === 'online' ? 'Click to show all' : 'Click to filter online only'}
            >
              <Wifi className={`w-3.5 h-3.5 ${visualTheme === 'cyber' ? 'text-green-400' : 'text-emerald-500'}`} />
              <span className={visualTheme === 'cyber' ? 'text-green-400 font-bold' : 'text-emerald-600 dark:text-emerald-400'}>
                {stats.online}
              </span>
            </button>
            <button
              data-testid="filter-offline-btn"
              onClick={() => toggleStatusFilter('offline')}
              className={`stats-widget cursor-pointer transition-all ${
                statusFilter === 'offline' 
                  ? visualTheme === 'cyber'
                    ? 'ring-2 ring-rose-400 ring-offset-2 ring-offset-background bg-rose-500/20 border-rose-400/50'
                    : 'ring-2 ring-rose-500 ring-offset-2 ring-offset-background bg-rose-500/10' 
                  : visualTheme === 'cyber'
                    ? 'hover:bg-rose-500/10 hover:border-rose-400/30'
                    : 'hover:bg-rose-500/10'
              } ${stats.offline === 0 ? 'opacity-50' : ''}`}
              title={statusFilter === 'offline' ? 'Click to show all' : 'Click to filter offline only'}
            >
              <WifiOff className={`w-3.5 h-3.5 ${visualTheme === 'cyber' ? 'text-rose-400' : 'text-rose-500'}`} />
              <span className={visualTheme === 'cyber' ? 'text-rose-400 font-bold' : 'text-rose-600 dark:text-rose-400'}>
                {stats.offline}
              </span>
            </button>
          </div>

          {/* Theme Toggle */}
          <Button
            data-testid="theme-toggle-btn"
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="h-8 w-8"
            disabled={visualTheme === 'cyber'}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {/* New Group Button */}
          <Button
            data-testid="add-group-btn"
            variant="ghost"
            size="sm"
            onClick={() => setShowAddGroup(true)}
            className={`h-8 gap-1.5 hidden sm:flex ${
              visualTheme === 'cyber' 
                ? 'border border-fuchsia-500/30 bg-fuchsia-500/5 hover:bg-fuchsia-500/10 hover:border-fuchsia-500/50 text-fuchsia-400'
                : ''
            }`}
          >
            <FolderPlus className="w-4 h-4" />
            <span>Group</span>
          </Button>

          {/* Add Server Button */}
          <Button
            data-testid="add-server-btn"
            onClick={() => setShowAddServer(true)}
            size="sm"
            className={`h-8 gap-1.5 ${
              visualTheme === 'cyber'
                ? 'border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-500/50 text-cyan-400'
                : ''
            }`}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add</span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="user-menu-btn"
                variant="ghost"
                className={`h-8 w-8 rounded-full p-0 ${
                  visualTheme === 'cyber' ? 'border border-cyan-500/30 hover:border-cyan-500/50' : ''
                }`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className={
                    visualTheme === 'cyber' 
                      ? 'bg-cyan-500/10 text-cyan-400 text-xs font-medium'
                      : 'bg-primary/10 text-primary text-xs font-medium'
                  }>
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-48" align="end">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                {user?.is_admin && (
                  <p className="text-xs text-primary font-medium mt-1">Admin</p>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowAddGroup(true)}>
                <Plus className="mr-2 h-4 w-4" />
                New Group
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <SettingsIcon className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      
      {/* Active Filter Banner */}
      {statusFilter && (
        <div className="bg-muted/50 border-b border-border px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Filtering:</span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                statusFilter === 'online' 
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                  : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
              }`}>
                {statusFilter === 'online' ? (
                  <><Wifi className="w-3 h-3" /> Online servers</>
                ) : (
                  <><WifiOff className="w-3 h-3" /> Offline servers</>
                )}
              </span>
              <span className="text-muted-foreground">
                ({filteredServers.length} of {servers.length})
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStatusFilter(null)}
              className="h-7 text-xs"
            >
              Clear filter
            </Button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto">
        <Routes>
          <Route
            path="/"
            element={
              <ServerGrid
                servers={filteredServers}
                groups={groups}
                onAddServer={() => setShowAddServer(true)}
                onAddGroup={() => setShowAddGroup(true)}
                onEditServer={setEditingServer}
                onDeleteServer={handleDeleteServer}
                onCheckServer={handleCheckServer}
                onViewServer={(id) => navigate(`/server/${id}`)}
                onDeleteGroup={handleDeleteGroup}
                onEditGroup={setEditingGroup}
                onToggleFavorite={handleToggleFavorite}
                onToggleCritical={handleToggleCritical}
                onReorderServers={handleReorderServers}
                onReorderGroups={handleReorderGroups}
              />
            }
          />
          <Route
            path="/server/:serverId"
            element={
              <ServerDetail
                servers={servers}
                groups={groups}
                onEditServer={setEditingServer}
                onDeleteServer={handleDeleteServer}
                onCheckServer={handleCheckServer}
                onBack={() => navigate("/")}
              />
            }
          />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>

      {/* Modals */}
      <AddServerModal
        open={showAddServer || !!editingServer}
        onClose={() => {
          setShowAddServer(false);
          setEditingServer(null);
        }}
        onSubmit={editingServer ? 
          (data) => handleUpdateServer(editingServer.id, data) : 
          handleAddServer
        }
        groups={groups}
        editingServer={editingServer}
      />

      <AddGroupModal
        open={showAddGroup}
        onClose={() => setShowAddGroup(false)}
        onSubmit={handleAddGroup}
      />

      <AddGroupModal
        open={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        onSubmit={handleEditGroup}
        editingGroup={editingGroup}
      />
    </div>
  );
};

export default Dashboard;
